/* eslint-disable react/forbid-prop-types */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { FiChevronDown, FiMapPin, FiSearch } from 'react-icons/fi';
import { sameDestination } from '../../utils/packageUtils';

const MAX_SHOWN = 60;

const sanitize = (value) =>
  String(value ?? '')
    .replace(/[^A-Za-z\s'(),.&-]/g, '')
    .replace(/\s{2,}/g, ' ')
    .replace(/^\s+/, '')
    .slice(0, 60);

/**
 * One picker over the catalogue's destinations (from available-facets), each
 * with how many packages it has. Type to filter; arrow keys + Enter work.
 * `value` is the destination label sent to search.
 */
function PackageDestinationField({
  value,
  onChange,
  destinations,
  loading,
  failed,
  onRetry,
  hasError,
  autoOpen,
}) {
  const [open, setOpen] = useState(autoOpen);
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);
  const rootRef = useRef(null);
  const listRef = useRef(null);

  useEffect(() => {
    if (!open) {
      setQuery('');
      setActive(0);
      return undefined;
    }
    const onDown = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target))
        setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  const results = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return destinations.slice(0, MAX_SHOWN);
    const starts = [];
    const contains = [];
    destinations.forEach((d) => {
      const name = d.label.toLowerCase();
      if (name.startsWith(q)) starts.push(d);
      else if (name.includes(q)) contains.push(d);
    });
    return [...starts, ...contains].slice(0, MAX_SHOWN);
  }, [destinations, query]);

  useEffect(() => {
    const node = listRef.current?.querySelector('[data-active="true"]');
    if (node) node.scrollIntoView({ block: 'nearest' });
  }, [active, results]);

  const select = (dest) => {
    if (!dest) return;
    onChange(dest.value);
    setOpen(false);
  };

  const onKeyDown = (e) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      setOpen(false);
      return;
    }
    if (!results.length) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActive((i) => (i + 1) % results.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActive((i) => (i - 1 + results.length) % results.length);
    } else if (e.key === 'Enter') {
      e.preventDefault();
      select(results[active] || results[0]);
    }
  };

  const current = destinations.find((d) => sameDestination(d.value, value));
  let hint = 'Where do you want to go?';
  if (current)
    hint = `${current.count} package${current.count === 1 ? '' : 's'}`;

  return (
    <div className="pkg-dropdown pkg-dest" ref={rootRef}>
      <button
        type="button"
        className={`pkg-field ${open ? 'pkg-field--open' : ''} ${
          hasError ? 'pkg-field--error' : ''
        }`}
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >
        <span className="pkg-field__label">
          <FiMapPin />
          Destination
        </span>
        <span
          className={`pkg-field__value ${
            current || value ? '' : 'pkg-field__value--placeholder'
          }`}
        >
          {current?.label || value || 'Select destination'}
        </span>
        <span className="pkg-field__hint">{hint}</span>
        <FiChevronDown className="pkg-field__caret" aria-hidden />
      </button>

      {open && (
        <div className="pkg-dropdown__panel pkg-dropdown__panel--left pkg-dest__panel">
          <div className="pkg-dest__search">
            <FiSearch />
            <input
              // eslint-disable-next-line jsx-a11y/no-autofocus
              autoFocus
              type="text"
              autoComplete="off"
              spellCheck={false}
              placeholder="Search destination"
              value={query}
              onChange={(e) => {
                setQuery(sanitize(e.target.value));
                setActive(0);
              }}
              onKeyDown={onKeyDown}
              aria-label="Search destination"
            />
          </div>
          <div className="pkg-dest__list" role="listbox" ref={listRef}>
            {loading && <div className="pkg-dest__empty">Loading…</div>}
            {!loading && failed && (
              <div className="pkg-dest__empty">
                Could not load destinations.
                {onRetry && (
                  <button type="button" onClick={onRetry}>
                    Retry
                  </button>
                )}
              </div>
            )}
            {!loading && !failed && results.length === 0 && (
              <div className="pkg-dest__empty">No matching destination</div>
            )}
            {!loading &&
              results.map((d, i) => {
                const selected = sameDestination(d.value, value);
                return (
                  <button
                    type="button"
                    key={d.value}
                    role="option"
                    aria-selected={selected}
                    data-active={i === active}
                    tabIndex={-1}
                    className={`pkg-dest__option ${
                      selected ? 'pkg-dest__option--selected' : ''
                    } ${i === active ? 'pkg-dest__option--active' : ''}`}
                    onMouseEnter={() => setActive(i)}
                    onClick={() => select(d)}
                  >
                    <FiMapPin />
                    <span className="pkg-dest__name">{d.label}</span>
                    <span className="pkg-dest__count">
                      {d.count} package{d.count === 1 ? '' : 's'}
                    </span>
                  </button>
                );
              })}
          </div>
        </div>
      )}
    </div>
  );
}

PackageDestinationField.propTypes = {
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  destinations: PropTypes.array.isRequired,
  loading: PropTypes.bool,
  failed: PropTypes.bool,
  onRetry: PropTypes.func,
  hasError: PropTypes.bool,
  autoOpen: PropTypes.bool,
};

PackageDestinationField.defaultProps = {
  loading: false,
  failed: false,
  onRetry: null,
  hasError: false,
  autoOpen: false,
};

export default PackageDestinationField;
